<template>
	<div v-if="loaded">
		<div :class="['c-opportunity-filters', { 'is-active': filtersActive }]">
			<div class="c-opportunity-filters__control">
				<h3>Filters</h3>
				<button
					class="c-opportunity-filters__close iconf-close"
					aria-label="Close Filter"
					@click="filtersActive = false"></button>
			</div>

			<div
				v-if="filters.categories"
				class="c-opportunity-filter">
				<button
					type="button"
					:class="[
						'c-opportunity-filter__heading',
						{ 'is-showing': showing.categories },
					]"
					@click="toggleShowing('categories')">
					Categories
				</button>
				<ul class="c-opportunity-filter__list">
					<p>Select categories</p>
					<li
						class="c-opportunity-filter__list-item"
						v-for="cat in filters.categories"
						:key="cat.id">
						<label>
							<input
								type="checkbox"
								:value="cat.name"
								:key="cat.id"
								v-model="selectedCategories" />
							{{ cat.name }}
						</label>
					</li>
				</ul>
			</div>

			<div
				v-if="filters.tenderTypes"
				class="c-opportunity-filter">
				<button
					type="button"
					:class="[
						'c-opportunity-filter__heading',
						{ 'is-showing': showing.tenderTypes },
					]"
					@click="toggleShowing('tenderTypes')">
					Tender Types
				</button>
				<ul class="c-opportunity-filter__list">
					<p>Select tender types</p>

					<li
						class="c-opportunity-filter__list-item"
						v-for="tender in filters.tenderTypes"
						:key="tender.id">
						<label>
							<input
								type="checkbox"
								:value="tender.name"
								:key="tender.id"
								v-model="selectedTenderTypes" />
							{{ tender.name }}
						</label>
					</li>
				</ul>
			</div>

			<div
				v-if="filters.statuses"
				class="c-opportunity-filter">
				<button
					type="button"
					:class="[
						'c-opportunity-filter__heading',
						{ 'is-showing': showing.statuses },
					]"
					@click="toggleShowing('statuses')">
					Status
				</button>
				<ul class="c-opportunity-filter__list">
					<p>Select opportunity status</p>
					<li
						class="c-opportunity-filter__list-item"
						v-for="status in filters.statuses"
						:key="status.id">
						<label>
							<input
								type="checkbox"
								:value="status.name"
								:key="status.id"
								v-model="selectedStatuses" />
							{{ status.name }}
						</label>
					</li>
				</ul>
			</div>

			<div class="c-opportunity-filter">
				<div class="c-opportunity-filter__search">
					<span
						class="c-opportunity-filter__search-button iconf-search"
						aria-label="Search"></span>
					<input
						class="c-opportunity-filter__search-input"
						type="text"
						name="name"
						v-model="searchKeyword"
						placeholder="Search" />
				</div>
			</div>
		</div>

		<div
			class="c-opportunity-filters-active"
			v-if="hasActiveFilters">
			<div class="c-opportunity-list__layout">
				<button
					v-for="cat in selectedCategories"
					:key="'cat-' + cat"
					class="c-opportunity-filters-active__item"
					type="button"
					@click="removeCategory(cat)">
					{{ cat }}
				</button>
				<button
					v-for="tender in selectedTenderTypes"
					:key="'tender-' + tender"
					class="c-opportunity-filters-active__item"
					type="button"
					@click="removeTenderType(tender)">
					{{ tender }}
				</button>
				<button
					v-for="status in selectedStatuses"
					:key="'status-' + status"
					class="c-opportunity-filters-active__item"
					type="button"
					@click="removeStatus(status)">
					{{ status }}
				</button>
				<button
					class="c-opportunity-filters-active__item-reset"
					type="reset"
					@click="resetAllFilters"
					aria-label="Reset all filters">
					Clear All Filters
				</button>
			</div>
		</div>

		<div class="c-opportunities">
			<div class="c-opportunity-list__layout">
				<button
					type="button"
					class="c-opportunity-filters__open"
					:aria-label="filterToggleAriaLabel"
					@click="filtersActive = !filtersActive">
					{{ filterToggleText }}
				</button>

				<ul
					v-if="filteredOpportunities.length"
					class="c-opportunity-list__listing">
					<li
						class="c-opportunity-list-item"
						v-for="(opportunity, idx) in filteredOpportunities"
						:key="opportunity.key || idx"
						:data-filter-category="
							opportunity.categoryType
								? opportunity.categoryType.name
								: ''
						"
						:data-filter-tendertype="
							opportunity.tenderType
								? opportunity.tenderType.name
								: ''
						"
						:data-filter-status="
							opportunity.status ? opportunity.status.name.id : ''
						"
						:data-filter-opendate="opportunity.openDate"
						:data-filter-closedate="opportunity.closeDate">
						<a
							class="c-opportunity-list-item__link"
							:href="opportunity.url">
							<div
								class="c-opportunity-list-item__block c-opportunity-list-item__block--icon">
								<span
									:class="[
										'c-opportunity-list-item__icon',
										opportunity.icon,
									]"></span>
							</div>
							<div
								class="c-opportunity-list-item__block c-opportunity-list-item__block--info">
								<span
									v-if="opportunity.tenderType"
									class="c-opportunity-list-item__code">
									{{
										opportunity.tenderType
											? opportunity.tenderType.code
											: ''
									}}
								</span>
								<span
									v-if="opportunity.referenceNumber"
									class="c-opportunity-list-item__ref"
									>{{ opportunity.referenceNumber }}</span
								>
								<span
									v-if="opportunity.categoryType"
									class="c-opportunity-list-item__category">
									{{
										opportunity.categoryType
											? opportunity.categoryType.name
											: ''
									}}
								</span>
							</div>
							<div class="c-opportunity-list-item__block">
								<h3
									v-if="opportunity.title"
									class="c-opportunity-list-item__title">
									{{ opportunity.title }}
								</h3>
								<p
									v-if="opportunity.abstract"
									class="c-opportunity-list-item__desc">
									{{ opportunity.abstract }}
								</p>
							</div>
							<div class="c-opportunity-list-item__block">
								<span
									:class="[
										'c-opportunity-list-item__status',
										opportunity.status
											? 'status-' + opportunity.status.id
											: '',
									]">
									{{
										opportunity.status
											? opportunity.status.name
											: ''
									}}
								</span>
								<div
									v-if="opportunity.closeDate"
									class="c-opportunity-list-item__closing">
									Closing Date:
									<span>{{
										formatOpportunityDate(
											opportunity.closeDate,
										)
									}}</span>
								</div>
							</div>
							<div
								class="c-opportunity-list-item__block c-opportunity-list-item__block--button">
								<div class="c-opportunity-list-item__button">
									View
								</div>
							</div>
						</a>
					</li>
				</ul>
				<p v-else>
					There are no opportunities matching your filter(s).
				</p>
			</div>
		</div>
	</div>
</template>

<script>
import info from 'Vue/opportunities-data';
export default {
	name: 'opportunitylisting',
	data() {
		return {
			loaded: false,
			opportunities: null,
			selectedCategories: [],
			selectedTenderTypes: [],
			selectedStatuses: [],
			filtersActive: false,
			filters: {},
			showing: {
				categories: false,
				tenderTypes: false,
				statuses: false,
			},
			searchKeyword: '',
		};
	},
	mounted() {
		// Fetch the opportunities data from the API
		fetch(info.api)
			.then((res) => res.json())
			.then((response) => {
				this.opportunities = response.opportunities || [];
				this.filters.categories =
					response.activeCategoryFilterOptions || [];
				this.filters.tenderTypes =
					response.activeTenderTypeFilterOptions || [];
				this.filters.statuses = response.statusOptions || [];
				this.loaded = true;
			})
			.catch(() => {
				this.opportunities = [];
			});

		document.addEventListener('click', this.handleClickOutside);
	},
	beforeUnmount() {
		document.removeEventListener('click', this.handleClickOutside);
	},
	computed: {
		filteredOpportunities() {
			const filtered = this.opportunities.filter((opportunity) => {
				// Category filter
				const allCategoriesSelected =
					this.selectedCategories.length === 0 ||
					this.selectedCategories.includes('');
				const categoryMatches =
					allCategoriesSelected ||
					(opportunity.categoryType &&
						this.selectedCategories.includes(
							opportunity.categoryType.name,
						));

				// Tender type filter
				const allTenderTypesSelected =
					this.selectedTenderTypes.length === 0 ||
					this.selectedTenderTypes.includes('');
				const tenderTypeMatches =
					allTenderTypesSelected ||
					(opportunity.tenderType &&
						this.selectedTenderTypes.includes(
							opportunity.tenderType.name,
						));

				// Status filter
				const statusId = opportunity.status.id;
				const allStatusesSelected =
					this.selectedStatuses.length === 0 ||
					this.selectedStatuses.includes('');
				const statusMatches =
					allStatusesSelected ||
					this.selectedStatuses.includes(statusId);

				// Keyword search
				const keyword = this.searchKeyword.trim().toLowerCase();
				const matchesSearch =
					!keyword ||
					(opportunity.title &&
						opportunity.title.toLowerCase().includes(keyword)) ||
					(opportunity.referenceNumber &&
						opportunity.referenceNumber
							.toLowerCase()
							.includes(keyword));

				return (
					categoryMatches &&
					tenderTypeMatches &&
					statusMatches &&
					matchesSearch
				);
			});

			// Sort: items with closeDate first, oldest to newest
			return filtered.sort((a, b) => {
				const closeDateA = a.closeDate ? new Date(a.closeDate) : null;
				const closeDateB = b.closeDate ? new Date(b.closeDate) : null;

				if (closeDateA && closeDateB) return closeDateA - closeDateB; // oldest to newest
				if (closeDateA && !closeDateB) return -1; // a first if it has date
				if (!closeDateA && closeDateB) return 1; // b first if it has date
				return 0; // both have no date
			});
		},
		hasActiveFilters() {
			return (
				this.selectedCategories.length > 0 ||
				this.selectedTenderTypes.length > 0 ||
				this.selectedStatuses.length > 0
			);
		},

		filterToggleText() {
			return this.filtersActive ? 'Close Filters' : 'Open Filters';
		},
		filterToggleAriaLabel() {
			return this.filtersActive ? 'Close Filter' : 'Open Filter';
		},
	},
	methods: {
		removeCategory(cat) {
			this.selectedCategories = this.selectedCategories.filter(
				(c) => c !== cat,
			);
		},
		removeTenderType(tender) {
			this.selectedTenderTypes = this.selectedTenderTypes.filter(
				(t) => t !== tender,
			);
		},
		removeStatus(status) {
			this.selectedStatuses = this.selectedStatuses.filter(
				(s) => s !== status,
			);
		},
		toggleShowing(group) {
			// Close all groups except the one clicked
			for (const key in this.showing) {
				this.showing[key] = key === group ? !this.showing[key] : false;
			}
		},
		handleClickOutside(event) {
			// Check if click happened outside of .c-opportunity-filter
			const clickedInside = event.target.closest('.c-opportunity-filter');
			if (!clickedInside) {
				for (const key in this.showing) {
					this.showing[key] = false;
				}
			}
		},
		resetAllFilters() {
			this.selectedCategories = [];
			this.selectedTenderTypes = [];
			this.selectedStatuses = [];
			this.searchKeyword = '';
		},
		formatOpportunityDate(dateStr) {
			if (!dateStr) return '';
			const date = new Date(dateStr);
			if (isNaN(date)) return dateStr;

			let hours = date.getHours();
			const minutes = date.getMinutes();
			const ampm = hours >= 12 ? 'pm' : 'am';
			hours = hours % 12 || 12;

			const hoursStr = String(hours).padStart(2, '0');
			const minutesStr = String(minutes).padStart(2, '0');

			const day = date.getDate();
			const monthNames = [
				'Jan',
				'Feb',
				'Mar',
				'Apr',
				'May',
				'Jun',
				'Jul',
				'Aug',
				'Sept',
				'Oct',
				'Nov',
				'Dec',
			];
			const month = monthNames[date.getMonth()];
			const year = date.getFullYear();

			return `${hoursStr}:${minutesStr} ${ampm}, ${day} ${month} ${year}`;
		},
	},
};
</script>
